import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const guideBenefits = [
  'Step-by-step Gibraltar wedding process',
  'Realistic costs and planning timelines',
  'Venue and supplier selection guidance',
  'Practical tips for UK and international couples',
];

export default function FreeGuidePage() {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isGuideEnabled, setIsGuideEnabled] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/cms/content`, {
          params: { _ts: Date.now() },
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
          },
        });
        setIsGuideEnabled(Boolean(response.data?.free_guide_enabled && response.data?.free_guide_pdf_storage_path));
      } catch {
        setIsGuideEnabled(false);
      }
    };

    fetchContent();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Please enter your name and email.');
      return;
    }

    if (!isGuideEnabled) {
      toast.error('The free guide is currently unavailable. Please try again later.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/free-guide-leads`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
      });
      setIsSubmitted(true);
      setFormData({ name: '', email: '' });
      toast.success('Great! We have your details.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#faf6f0_0%,#ffffff_35%)]" data-testid="free-guide-page">
      <section className="py-20">
        <div className="container-wedding max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="caption mb-3" data-testid="free-guide-caption">Free planning guide</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-stone-900 mb-5" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="free-guide-title">
              Your Free Gibraltar Wedding Guide
            </h1>
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-6" data-testid="free-guide-value-copy">
              Start your wedding journey with clear, practical guidance designed for couples planning in Gibraltar.
            </p>

            <ul className="space-y-3" data-testid="free-guide-benefits-list">
              {guideBenefits.map((item) => (
                <li key={item} className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                  <CheckCircle2 className="h-5 w-5 text-[#D4AF37] mt-0.5" />
                  <span className="text-stone-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-[28px] border border-stone-200 bg-white p-7 shadow-[0_20px_60px_rgba(28,25,23,0.05)]" data-testid="free-guide-form-card">
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" data-testid="free-guide-form-title">Get the guide by email</h2>
            <p className="text-stone-600 mb-6" data-testid="free-guide-form-subtitle">Enter your details and we’ll send it straight to your inbox.</p>

            {isGuideEnabled ? (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="free-guide-capture-form">
              <div className="space-y-2">
                <Label htmlFor="free-guide-name" data-testid="free-guide-name-label">Full name</Label>
                <Input
                  id="free-guide-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  autoComplete="name"
                  data-testid="free-guide-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="free-guide-email" data-testid="free-guide-email-label">Email address</Label>
                <Input
                  id="free-guide-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  autoComplete="email"
                  data-testid="free-guide-email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#5C0120] hover:bg-[#5C0120]/90 text-white uppercase tracking-[0.14em] font-bold"
                disabled={isSubmitting}
                data-testid="free-guide-submit-button"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Guide'}
              </Button>
              </form>
            ) : (
              <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 text-stone-700" data-testid="free-guide-unavailable-message">
                The free guide is currently unavailable. Please check back soon.
              </div>
            )}

            {isSubmitted && (
              <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="free-guide-success-message">
                Thank you — your request has been received. We’ll send your guide shortly.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}